/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../components/appNav/AppHeaderScrollable';

import AmountInput from './components/AmountInput';
import FrequencyInput from './components/FrequencyInput';
import ContentWrapper from '../../components/ContentWrapper';
import HEAttributeFooter from './components/HEAttributeFooter';
import ContentLoader from '../../components/ContentLoader';

import HealthEntryModel from '../../../models/HealthEntry';
import { useHealthEntryForActiveProfile } from '../../hooks/useHealthEntry';
import { useNavigate } from '../../hooks/useNavigate';
import { useUIState } from '../../hooks/useUIState';
import { useRelatedUnits, useFrequencyList } from '../../hooks/useMedli';
import RouteNames from '../../appAuthorized/RouteNames';
import { isValidMedicationAmount } from '../../../utilities/Validators';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';

interface Props {
  match: any;
}

const checkInvalid = (pendingHeInstance) => {
  const hasAmount = pendingHeInstance.amount && pendingHeInstance.amount.hasAmount();
  return hasAmount ? (!isValidMedicationAmount(pendingHeInstance.amount.getValue()) || !pendingHeInstance.amount.hasUnit()) : false;
};
const HealthEntryAmountAndFrequency: React.FC<Props> = (props) => {
  // Medications are currently the only HE that use this view
  const healthEntryId = Number(props.match.params.healthEntryId);

  const [{ category }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  const sourceHeInstance = heActions.instantiateHealthEntry();
  const pendingHeInstance = heActions.instantiateEditingHealthEntry();
  const cui = HealthEntryModel.getCUI(pendingHeInstance.data());

  const [relatedUnitsState] = useRelatedUnits(category, cui);
  const [, freqActions] = useFrequencyList();
  const [, navActions] = useNavigate();
  const [, uiStateActions] = useUIState();

  const isDataLoaded = relatedUnitsState.isLoaded;

  const frequencyData = HealthEntryModel.getFrequency(pendingHeInstance.data());
  const currentFrequency = freqActions.get(frequencyData.id) || {};

  const hasAmountValue = sourceHeInstance.amount && sourceHeInstance.amount.hasAmount();
  const hasFrequency =  sourceHeInstance.frequency && sourceHeInstance.frequency.hasFrequency();
  const isDeletable = hasAmountValue || hasFrequency;
  const hasAmountProperty = Boolean(pendingHeInstance.amount);
  const isInvalid = checkInvalid(pendingHeInstance);

  const showAmount = category === HealthEntryCategoryNames.medication;

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={isInvalid}
          cancel={() => {
            heActions.clearEditing();
            navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
          }}
          onDisabledClick={() => {
            if (pendingHeInstance.amount && !pendingHeInstance.amount.hasUnit()) {
              uiStateActions.showHintNotification('error.amtFreq-validator.no-selected-amount-measure.message');
            }
          }}
          submit={() => {
            heActions.apiUpdate(pendingHeInstance.data()).then(() => {
              heActions.clearEditing();
              navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
            }).catch(apiError => {
              uiStateActions.showErrorNotification(apiError.messageKey());
            });
          }}
          color="white"
        />

        <ContentFrame>
          <ScrollAwareScroller>
            <AppHeaderScrollableText
              id={hasAmountProperty ? 'amtFreq.amtAndFreq.title.text' : 'amtFreq.freqOnly.title.text'}
            />
            <ContentLoader isContentLoaded={isDataLoaded} isFixed={true} blockRenderUntilLoaded={true}>
              <ContentWrapper>
                {
                  showAmount && (
                    <AmountInput
                      cui={cui}
                      category={category}
                      healthEntryId={pendingHeInstance.id}
                      healthEntryValueId={null}
                      amount={pendingHeInstance.amount ? pendingHeInstance.amount.get() : ''}
                      onChangeAmount={(amountData) => {
                        const newHE = heActions.instantiateEditingHealthEntry();
                        newHE.amount.set(amountData);
                        heActions.updateEditing(newHE.data());
                      }}
                      allowCompositeUnit={false}
                      haveInitialFocus={false}
                      textPhrase={{
                        addUnit: {
                          id: 'amtFreq.unit.addUnit.label',
                        },
                        changeUnit: {
                          id: 'amtFreq.unit.changeUnit.label',
                        },
                        search: {
                          id: 'amtFreq.unit.search.label',
                        },
                        inputLabel: {
                          id: 'amtFreq.amount.field.label',
                        },
                      }}
                      valueValidation={(v) => {
                        return {
                          isValid: isValidMedicationAmount(v),
                          errorMessage: {
                            id: 'error.number-validator.pos-number-or-range.message',
                          },
                        };
                      }}
                    />
                  )
                }

                <div>
                  <FrequencyInput
                    frequencyId={frequencyData.id || null}
                    value={frequencyData.value}
                    selectedFrequency={currentFrequency}
                    goToFrequencyUnits={(frequencyGroup) => {
                      navActions.goTo(RouteNames.healthEntryFrequencyUnit(healthEntryId, frequencyGroup));
                    }}
                    onChangeFrequency={(newFrequencyData) => {
                      const newHE = heActions.instantiateEditingHealthEntry();
                      const selectedSameFrequency = currentFrequency.id === newFrequencyData.id && currentFrequency.value === newFrequencyData.value;
                      if (selectedSameFrequency) {
                        newHE.frequency.clear('');
                      } else {
                        newHE.frequency.set(newFrequencyData);
                      }

                      heActions.updateEditing(newHE.data());
                    }}
                  />
                </div>
                {
                  isDeletable && (
                    <HEAttributeFooter
                      onDelete={() => {
                        const newHE = heActions.instantiateEditingHealthEntry();

                        if (newHE.amount) {
                          newHE.amount.clear();
                        }
                        newHE.frequency.clear();
                        heActions.apiUpdate(newHE.data()).then(() => {
                          heActions.clearEditing();
                          navActions.goBackTo(RouteNames.healthEntry(healthEntryId));
                        });
                      }}
                      onToggleFlag={null}
                    />
                  )
                }
              </ContentWrapper>

            </ContentLoader>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

export default HealthEntryAmountAndFrequency;
